import { DateTime } from "luxon";
import { makePlayer } from "../play-tests/model";
import { getRulebookExportName } from "./rulebookImportUtils";

/** @type {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentType} */
export const NESTIFYZ_SCORE_METADATA_TYPE = "SCORE";

export const NESTIFYZ_SCORE_METADATA_NAME = "Score";

/**
 * NestiFyz `playtestType` (and related labels) mapped to Pitch2Table `PlayTestAudience`.
 *
 * @type {Record<string, import("@seabrookstudios/pitch2table-core").PlayTestAudience>}
 */
export const NESTIFYZ_PLAY_TEST_AUDIENCE_MAP = {
  "Internal (Friends & Family)": "FriendsFamily",
  "Blind (No Help/Explanation)": "Blind",
  "Local (Strangers)": "Gamers",
};

/**
 * NestiFyz values that indicate where the session was played.
 *
 * @type {Record<string, import("@seabrookstudios/pitch2table-core").PlayTestMedium>}
 */
export const NESTIFYZ_PLAYTEST_MEDIUM_MAP = {
  "In-person": "Physical",
  Online: "Digital",
};

/**
 * @param {import("./parseExport").NestiFyzPlayTest} playtest
 * @returns {number}
 */
export const getPlaytestDateMillis = (playtest) => {
  const candidate = playtest.playtestDate || playtest.playtestStartTime || playtest.createdAt;

  if (!candidate) {
    return DateTime.now().toMillis();
  }

  const parsed = DateTime.fromISO(candidate);

  return parsed.isValid ? parsed.toMillis() : DateTime.now().toMillis();
};

/**
 * @param {import("./parseExport").NestiFyzPlayTest} playtest
 * @returns {number|undefined}
 */
export const getPlaytestDurationMinutes = (playtest) => {
  const start = playtest.playtestStartTime || playtest.rulesExplanationStartTime || playtest.setupStartTime;
  const end = playtest.playtestEndTime || playtest.rulesExplanationEndTime || playtest.setupEndTime;

  if (!start || !end) {
    return undefined;
  }

  const startAt = DateTime.fromISO(start);
  const endAt = DateTime.fromISO(end);

  if (!startAt.isValid || !endAt.isValid || endAt.toMillis() <= startAt.toMillis()) {
    return undefined;
  }

  return Math.round(endAt.diff(startAt, "minutes").minutes);
};

/**
 * @param {import("./parseExport").NestiFyzPlayTest[]} [playtests]
 * @returns {import("./parseExport").NestiFyzPlayTest[]}
 */
export const getImportablePlaytests = (playtests = []) => {
  return [...playtests].sort((a, b) => getPlaytestDateMillis(a) - getPlaytestDateMillis(b));
};

/**
 * @param {import("./parseExport").NestiFyzPlayTest} playtest
 * @returns {File[]}
 */
export const getImportablePlaytestPhotos = (playtest) => {
  return playtest.mediaFiles || [];
};

/**
 * @param {string|undefined} location
 * @returns {string|undefined}
 */
const getLocationFilename = (location) => {
  if (!location) {
    return undefined;
  }

  return location.split("/").pop()?.split("?")[0];
};

/**
 * @param {{name?: string, s3location?: string, bytes?: number, mimeType?: string, deleted?: boolean}} photo
 * @param {File} file
 * @returns {boolean}
 */
export const photoMatchesImportFile = (photo, file) => {
  if (photo.deleted) {
    return false;
  }

  if (photo.name === file.name) {
    return true;
  }

  const locationFilename = getLocationFilename(photo.s3location);

  if (locationFilename && locationFilename === file.name) {
    return true;
  }

  if (photo.bytes === file.size && photo.mimeType === file.type) {
    return true;
  }

  return false;
};

/**
 * @param {Array<{name?: string, s3location?: string, bytes?: number, mimeType?: string, deleted?: boolean}>} photos
 * @param {File} file
 * @returns {boolean}
 */
export const hasExistingImportedPlaytestPhoto = (photos, file) => {
  return photos.some((photo) => photoMatchesImportFile(photo, file));
};

/**
 * @param {import("./parseExport").NestiFyzPlayTest} playtest
 * @returns {string}
 */
export const getPlaytestProgressLabel = (playtest) => {
  if (playtest.playtestName) {
    return playtest.playtestName;
  }

  const date = playtest.playtestDate || playtest.playtestStartTime;

  if (date) {
    const parsed = DateTime.fromISO(date);

    if (parsed.isValid) {
      return parsed.toFormat("dd MMM yyyy");
    }
  }

  return "Play test";
};

const NotesHeader = "## Notes";
const IdeasHeader = "## Ideas";
const ObservationsHeader = "## Observations";

/**
 * @param {import("./parseExport").NestiFyzPlayTest} playtest
 * @returns {string}
 */
export const buildNestiFyzPlaytestBody = (playtest) => {
  const sections = [`Nestifyz Playtest Id: ${playtest.id}`];

  if (playtest.playtestName) {
    sections.push(`# ${playtest.playtestName}`);
  }

  const playTestNotes = playtest.notes || [];
  const playTestIdeas = playtest.ideas || [];
  const playTestObservations = playtest.observations || [];

  const notes = [
    playTestNotes.length > 0 ? NotesHeader : undefined,
    ...playTestNotes,
    playTestIdeas.length > 0 ? IdeasHeader : undefined,
    ...playTestIdeas,
    playTestObservations.length > 0 ? ObservationsHeader : undefined,
    ...playTestObservations,
  ].filter((section) => !!section);

  if (notes.length > 0) {
    sections.push(notes.join("\n\n"));
  }

  const body = sections.join("\n\n").trim();

  return body;
};

/**
 * @param {import("./parseExport").NestiFyzPlaytestParticipant[]} participants
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId|undefined} scoreMetadataId
 * @returns {import("@seabrookstudios/pitch2table-core").PlayTestPlayer[]}
 */
export const buildNestiFyzPlaytestOutcomes = (participants = [], scoreMetadataId) => {
  return (participants || []).map((participant) => {
    const name = participant.name?.trim() || "Anonymous";

    return {
      ...makePlayer(name),
      won: !!participant.isWinner,
      scores:
        scoreMetadataId != null && participant.score != null ? [{ id: scoreMetadataId, value: participant.score }] : [],
      anonymous: name === "Anonymous",
    };
  });
};

/**
 * @param {import("./parseExport").NestiFyzRulebook[]} rulebooks
 * @param {import("@seabrookstudios/pitch2table-core").Rules[]} importedRules
 * @param {string|undefined} nestifyzRulebookId
 * @returns {import("@seabrookstudios/pitch2table-core").AttachmentId|undefined}
 */
export const resolveImportedRulesVersion = (rulebooks = [], importedRules, nestifyzRulebookId) => {
  if (!nestifyzRulebookId) {
    return undefined;
  }

  const nestifyzRulebook = rulebooks.find((rulebook) => rulebook.id === nestifyzRulebookId);
  if (!nestifyzRulebook) {
    return undefined;
  }

  const exportName = getRulebookExportName(nestifyzRulebook);

  if (!exportName) {
    return undefined;
  }

  return importedRules.find((rulesDoc) => rulesDoc.name === exportName)?.id;
};

/**
 * Attempts to find an existing playtest that matches the given NestiFyz playtest. We do this by matching the date and the number of participants.
 * @param {import("@seabrookstudios/pitch2table-core").PlayTest[]} existingPlaytests
 * @param {import("./parseExport").NestiFyzPlayTest} playtest
 * @returns {import("@seabrookstudios/pitch2table-core").PlayTest|undefined}
 */
export const findExistingImportedPlaytest = (existingPlaytests, playtest) => {
  const date = getPlaytestDateMillis(playtest);
  const participantCount = (playtest.participants || []).length;

  return existingPlaytests.find(
    (existingPlaytest) =>
      existingPlaytest.date === date && (existingPlaytest.outcomes || []).length === participantCount
  );
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponent[]} metadata
 * @returns {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponent|undefined}
 */
export const findNestiFyzScoreMetadata = (metadata) => {
  return metadata.find((component) => component.type === NESTIFYZ_SCORE_METADATA_TYPE && !component.archived);
};

export const NESTIFYZ_FEEDBACK_MARKER_PREFIX = "Nestifyz Feedback:";

/**
 * @param {import("./parseExport").NestiFyzPlayTest} playtest
 * @returns {import("./parseExport").NestiFyzPlaytestFeedback[]}
 */
export const getImportableFeedbacks = (playtest) => {
  return (playtest.feedbacks || []).filter((feedback) => (feedback.responses || []).length > 0);
};

/**
 * @param {import("./parseExport").NestiFyzPlaytestFeedback} feedback
 * @returns {string}
 */
export const buildNestiFyzPlaytestFeedbackMarker = (feedback) => {
  const name = feedback.playtesterName?.trim() || "Anonymous";

  return `${NESTIFYZ_FEEDBACK_MARKER_PREFIX} ${name}`;
};

/**
 * @param {import("./parseExport").NestiFyzPlaytestFeedback} feedback
 * @returns {string}
 */
export const buildNestiFyzPlaytestFeedbackBody = (feedback) => {
  const sections = [buildNestiFyzPlaytestFeedbackMarker(feedback)];

  const name = feedback.playtesterName?.trim();

  if (name) {
    sections.push(`# Feedback from ${name}`);
  } else {
    sections.push("# Playtester feedback");
  }

  if (feedback.status) {
    sections.push(`**Status:** ${feedback.status}`);
  }

  for (const response of feedback.responses || []) {
    if (!response.question) {
      continue;
    }

    sections.push(`### ${response.question}\n\n${response.answer || ""}`);
  }

  return sections.join("\n\n");
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").Message[]} messages
 * @param {import("./parseExport").NestiFyzPlaytestFeedback} feedback
 * @returns {boolean}
 */
export const findExistingImportedFeedback = (messages, feedback) => {
  const marker = buildNestiFyzPlaytestFeedbackMarker(feedback);

  return messages.some((message) => typeof message.body === "string" && message.body.includes(marker));
};
